/**
 * Данные — только владелец: откуда и насколько свежие цифры, аварийный
 * выключатель сбора (§8.10 ТЗ финансового агента) и расход на ИИ против
 * месячного потолка (§12.2 ТЗ платформы).
 *
 * Потолок расхода отсюда не меняется: он задаётся командой
 * `make llm-budget` — экрана у этого решения пока нет.
 */

import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { ApiError } from '@shell/api';
import { ErrorState, Loading } from '@shell/states';
import { Notice, when } from '../../ui';
import { SETTINGS_KEYS, problemText, settingsApi } from './api';
import type { Collection, FinanceData, SystemState } from './api';

const SERVICE_NAMES: Record<string, string> = {
  all: 'Всего по системе',
  ask: 'Поле «Спросить»',
  accountant: 'Бухгалтер-калькулятор',
  ops: 'Операционный агент',
  finance: 'Финансовый агент',
  study: 'Обучающий агент',
};

const ENTITY_NAMES: Record<string, string> = {
  incoming_documents: 'приходные накладные',
  incoming_document_rows: 'строки накладных',
  counterparties: 'поставщики',
  departments: 'склады и подразделения',
  goods: 'товары склада',
  goods_prices: 'цены закупки',
  goods_remains: 'остатки',
  writeoffs: 'списания',
  shifts: 'смены кассы',
  checks: 'чеки',
  check_items: 'позиции чеков',
  check_payments: 'оплаты чеков',
};

type Result = { ok: boolean; text: string } | null;

export function Data() {
  return (
    <div className="set-stack">
      <Freshness />
      <CollectionSwitch />
      <Channel />
      <Budget />
    </div>
  );
}

function Freshness() {
  const query = useQuery<FinanceData, ApiError>({
    queryKey: SETTINGS_KEYS.financeData,
    queryFn: () => settingsApi.financeData(),
    refetchInterval: 60_000,
  });
  return (
    <section className="set-card" aria-label="Свежесть цифр">
      <h2 className="set-card__title">Свежесть цифр</h2>
      {query.isPending ? (
        <Loading />
      ) : query.error ? (
        <ErrorState error={query.error} onRetry={() => query.refetch()} />
      ) : (
        <>
          <p className={`set-value${query.data.freshness.stale ? ' set-value--warn' : ''}`}>
            {query.data.freshness.headline}
          </p>
          {query.data.freshness.details.length > 0 && (
            <ul className="set-bullets">
              {query.data.freshness.details.map((line) => (
                <li key={line}>{line}</li>
              ))}
            </ul>
          )}
        </>
      )}
    </section>
  );
}

function CollectionSwitch() {
  const client = useQueryClient();
  const [result, setResult] = useState<Result>(null);
  const query = useQuery<Collection, ApiError>({
    queryKey: SETTINGS_KEYS.collection,
    queryFn: () => settingsApi.collection(),
  });
  const save = useMutation<Collection, ApiError, boolean>({
    mutationFn: (paused) => settingsApi.setCollection(paused),
    onSuccess: (data) => {
      client.setQueryData(SETTINGS_KEYS.collection, data);
      setResult({ ok: true, text: data.message ?? 'Сохранено.' });
    },
    onError: (error) => setResult({ ok: false, text: problemText(error) }),
  });

  return (
    <section className="set-card" aria-label="Сбор данных">
      <h2 className="set-card__title">Сбор данных из кассы и склада</h2>
      {query.isPending ? (
        <Loading />
      ) : query.error ? (
        <ErrorState error={query.error} onRetry={() => query.refetch()} />
      ) : (
        <>
          {query.data.text.split('\n\n').map((paragraph) => (
            <p key={paragraph} className="set-hint">
              {paragraph}
            </p>
          ))}
          <div className="set-inline">
            <button
              type="button"
              className={`button ${query.data.paused ? 'button--primary' : 'button--danger'}`}
              disabled={save.isPending}
              onClick={() => {
                setResult(null);
                save.mutate(!query.data.paused);
              }}
            >
              {query.data.paused ? 'Возобновить сбор' : 'Остановить сбор'}
            </button>
          </div>
        </>
      )}
      {result && <Notice kind={result.ok ? 'ok' : 'error'}>{result.text}</Notice>}
    </section>
  );
}

function useSystem() {
  return useQuery<SystemState, ApiError>({
    queryKey: SETTINGS_KEYS.system,
    queryFn: () => settingsApi.system(),
    refetchInterval: 60_000,
  });
}

function Channel() {
  const query = useSystem();
  return (
    <section className="set-card" aria-label="Служба в ресторане">
      <h2 className="set-card__title">Служба в ресторане</h2>
      <p className="set-hint">
        Забирает данные из StoreHouse и кассы и привозит их сюда.
        {query.data?.stale_after_minutes
          ? ` Данные считаются устаревшими после ${query.data.stale_after_minutes} мин молчания.`
          : ''}
      </p>
      {query.isPending ? (
        <Loading />
      ) : query.error ? (
        <ErrorState error={query.error} onRetry={() => query.refetch()} />
      ) : query.data.collectors.length === 0 ? (
        <p className="set-value">Ни один ресторан не подключён.</p>
      ) : (
        query.data.collectors.map((item) => (
          <div className="set-group" key={item.code}>
            <div className="set-row__head">
              <strong>{item.title ?? item.code}</strong>
              {!item.active && <span className="pill pill--red">отключён</span>}
              {item.version && <span className="set-row__hint">версия {item.version}</span>}
            </div>
            <ul className="set-list">
              <li className="set-row set-row--plain">
                <span className="set-row__title">Выходила на связь</span>
                <span className="set-row__value">{when(item.last_seen_at) || 'никогда'}</span>
              </li>
              <li className="set-row set-row--plain">
                <span className="set-row__title">Привозила данные</span>
                <span className="set-row__value">{when(item.last_batch_at) || 'никогда'}</span>
              </li>
              {item.entities.map((entity) => (
                <li className="set-row set-row--plain" key={entity.entity}>
                  <span className="set-row__title">
                    {ENTITY_NAMES[entity.entity] ?? entity.entity}
                    <span className="set-row__hint"> · строк: {entity.rows}</span>
                  </span>
                  <span className={`set-row__value${entity.stale ? ' set-value--warn' : ''}`}>
                    {when(entity.received_at) || '—'}
                    {entity.stale ? ' · устарело' : ''}
                  </span>
                </li>
              ))}
            </ul>
            {item.last_error && (
              <Notice kind="error">
                Последняя ошибка{item.last_error_at ? ` (${when(item.last_error_at)})` : ''}:{' '}
                {item.last_error}
              </Notice>
            )}
          </div>
        ))
      )}
    </section>
  );
}

function usd(value: string | null): string {
  if (value === null) return '—';
  return `$${Number(value).toLocaleString('ru-RU', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function Budget() {
  const query = useSystem();
  return (
    <section className="set-card" aria-label="Расход на ИИ">
      <h2 className="set-card__title">Расход на ИИ в этом месяце</h2>
      <p className="set-hint">
        Бюджет один на всю систему. При 80% придёт уведомление, при 100% агенты перейдут на
        упрощённые ответы и продолжат работать; за сутки больше обычного — сообщение сразу.
        Бюджет меняет разработчик командой make llm-budget.
      </p>
      {query.isPending ? (
        <Loading />
      ) : query.error ? (
        <ErrorState error={query.error} onRetry={() => query.refetch()} />
      ) : (
        <ul className="set-list">
          {query.data.llm.map((item) => (
            <li className="set-row set-row--budget" key={item.service}>
              <div className="set-row__main">
                <span className="set-row__title">{SERVICE_NAMES[item.service] ?? item.service}</span>
                <span className="set-row__hint">
                  {usd(item.spent_usd)}
                  {item.limit_usd !== null ? ` из ${usd(item.limit_usd)}` : ''}
                  {item.exhausted ? ' · потолок достигнут' : item.over_80 ? ' · больше 80%' : ''}
                </span>
              </div>
              {item.percent !== null && (
                <div
                  className={`set-meter${item.exhausted ? ' set-meter--full' : item.over_80 ? ' set-meter--warn' : ''}`}
                  role="meter"
                  aria-valuemin={0}
                  aria-valuemax={100}
                  aria-valuenow={Math.min(100, item.percent)}
                  aria-label={`Израсходовано: ${item.percent}%`}
                >
                  <span style={{ width: `${Math.min(100, item.percent)}%` }} />
                </div>
              )}
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
